/**
 * Utility to create helpful, actionable error messages for users
 */

export interface ActionableError {
  title: string;
  message: string;
  actions?: Array<{
    label: string;
    action: () => void;
  }>;
  helpUrl?: string;
}

/**
 * Convert various error types into user-friendly actionable errors
 */
export function createActionableError(error: unknown, context?: string): ActionableError {
  // Network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      title: 'Connection Error',
      message: 'Unable to connect to Humblytics servers. Please check your internet connection and try again.',
      actions: [
        {
          label: 'Retry',
          action: () => window.location.reload(),
        },
      ],
    };
  }

  // API errors with status codes
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as any).status;
    
    if (status === 401 || status === 403) {
      return {
        title: 'Authentication Error',
        message: 'Your session has expired or you don\'t have permission to edit this test. Please log in again.',
        actions: [
          {
            label: 'Go to Dashboard',
            action: () => window.location.href = 'https://app.humblytics.com',
          },
        ],
      };
    }

    if (status === 404) {
      return {
        title: 'Test Not Found',
        message: 'This A/B test no longer exists or you don\'t have access to it.',
        actions: [
          {
            label: 'View All Tests',
            action: () => window.location.href = 'https://app.humblytics.com/tests',
          },
        ],
      };
    }

    if (status === 429) {
      return {
        title: 'Too Many Requests',
        message: 'You\'re making changes too quickly. Please wait a moment and try again.',
      };
    }

    if (status >= 500) {
      return {
        title: 'Server Error',
        message: 'Humblytics servers are experiencing issues. Your changes have been saved locally and will sync when the connection is restored.',
        actions: [
          {
            label: 'Contact Support',
            action: () => window.open('mailto:support@humblytics.com', '_blank'),
          },
        ],
      };
    }
  }

  // Validation errors
  if (error instanceof Error && error.message.includes('selector')) {
    return {
      title: 'Invalid Element',
      message: 'The element you\'re trying to edit is no longer valid. This can happen if the page structure changed. Please refresh and try again.',
      actions: [
        {
          label: 'Refresh Page',
          action: () => window.location.reload(),
        },
      ],
    };
  }

  // AI errors
  if (error instanceof Error && error.message.toLowerCase().includes('ai')) {
    return {
      title: 'AI Service Unavailable',
      message: 'Pixel (our AI assistant) is temporarily unavailable. You can still make manual edits, or try again in a moment.',
      actions: [
        {
          label: 'Retry',
          action: () => window.location.reload(),
        },
      ],
    };
  }

  // Save errors
  if (context === 'save') {
    return {
      title: 'Save Failed',
      message: error instanceof Error 
        ? error.message 
        : 'Unable to save your changes. They\'re stored locally and will be synced when possible.',
      actions: [
        {
          label: 'Try Again',
          action: () => {}, // Will be overridden by caller
        },
        {
          label: 'Download Backup',
          action: () => {
            // Create a JSON backup
            const backup = {
              timestamp: new Date().toISOString(),
              error: error instanceof Error ? error.message : String(error),
              // Instructions would be added by caller
            };
            const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `humblytics-backup-${Date.now()}.json`;
            a.click();
            URL.revokeObjectURL(url);
          },
        },
      ],
    };
  }

  // Generic error
  return {
    title: 'Something Went Wrong',
    message: error instanceof Error 
      ? error.message 
      : 'An unexpected error occurred. Please try again or contact support if the issue persists.',
    actions: [
      {
        label: 'Contact Support',
        action: () => {
          const subject = encodeURIComponent('Editor Error Report');
          const body = encodeURIComponent(
            `I encountered an error in the Humblytics editor:\n\n` +
            `Error: ${error instanceof Error ? error.message : String(error)}\n` +
            `Context: ${context || 'Unknown'}\n` +
            `Timestamp: ${new Date().toISOString()}\n` +
            `URL: ${window.location.href}`
          );
          window.open(`mailto:support@humblytics.com?subject=${subject}&body=${body}`, '_blank');
        },
      },
    ],
    helpUrl: 'https://docs.humblytics.com/troubleshooting',
  };
}

/**
 * Format error for display in toast notifications
 */
export function formatErrorForToast(error: unknown, context?: string): { message: string; description?: string } {
  const actionable = createActionableError(error, context);
  return {
    message: actionable.title,
    description: actionable.message,
  };
}

